import { useQuery } from '@tanstack/react-query';
import type {
  CartonDto,
  ConfirmPickResult,
  OrderDetailDto,
  OrderSummaryDto,
  Page,
  PartnerDto,
  PickListDetailDto,
  PickListDto,
  PickMethod,
  PickTaskDto,
  ReplenTaskDto,
  WaveDto,
} from '@zenarty/shared';
import {
  ArrowLeft,
  Boxes,
  CheckCircle2,
  ClipboardList,
  Layers,
  PackageCheck,
  PackageOpen,
  Play,
  Printer,
  RefreshCcw,
  ScanLine,
  Send,
  Truck,
  Waves,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { LpnLabelDialog } from '../components/barcode';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  PageHeader,
  Skeleton,
  Stat,
  Tabs,
} from '../components/kit';
import { Dialog } from '../components/overlays';
import { ReasonSelect } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { useToast } from '../components/toast';
import { formatNumber, timeAgo } from '../lib/format';
import { useAction, useClients, useSites } from '../lib/queries';
import { Progress } from './Inbound';
import { CartonsTable, PicksTable } from './OrderDetail';
import { cutoffLabel, OrderStatusBadge } from './Orders';

const METHOD: Record<PickMethod, { label: string; help: string }> = {
  discrete: { label: 'Discrete', help: 'One order per pick list' },
  batch: { label: 'Batch', help: 'All orders on one list, sorted by location' },
  cluster: { label: 'Cluster', help: 'A cart of N orders, one tote slot each' },
  zone: { label: 'Zone', help: 'One list per zone, pick and pass' },
};

function useDefaultSite() {
  const sites = useSites();
  const [siteId, setSiteId] = useState('');
  useEffect(() => {
    if (!siteId && sites.data?.items[0]) setSiteId(sites.data.items[0].id);
  }, [siteId, sites.data]);
  const select = (
    <select
      className="select"
      aria-label="Site"
      value={siteId}
      onChange={(e) => setSiteId(e.target.value)}
    >
      {(sites.data?.items ?? []).map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
  return { siteId, select };
}

// ------------------------------------------------------------ waves

export function WavesPage() {
  const { can } = useAuth();
  const { siteId, select } = useDefaultSite();
  const [planning, setPlanning] = useState(false);
  const [status, setStatus] = useState<'planned' | 'released' | 'complete' | ''>('');
  const q = useQuery({
    queryKey: ['picking', 'waves', siteId, status],
    queryFn: () => api<WaveDto[]>(`/waves?siteId=${siteId}${status ? `&status=${status}` : ''}`),
    enabled: Boolean(siteId),
    refetchInterval: 30_000,
  });
  const release = useAction((id: string) => api(`/waves/${id}/release`, { method: 'POST' }), {
    invalidate: [['picking'], ['orders']],
    success: 'Wave released to the floor',
  });
  const cancel = useAction((id: string) => api(`/waves/${id}/cancel`, { method: 'POST' }), {
    invalidate: [['picking'], ['orders']],
    success: 'Wave cancelled',
  });
  const columns: Column<WaveDto>[] = [
    {
      id: 'n',
      header: 'Wave',
      cell: (w) => (
        <div>
          <div className="primary-cell mono">{w.number}</div>
          <div className="sub">
            {w.createdBy} · {timeAgo(w.createdAt)}
          </div>
        </div>
      ),
    },
    {
      id: 'm',
      header: 'Method',
      cell: (w) => (
        <div>
          {METHOD[w.pickMethod].label}
          <div className="sub">{w.lists} list(s)</div>
        </div>
      ),
    },
    {
      id: 'o',
      header: 'Orders',
      align: 'right',
      cell: (w) => <span className="num">{w.orders}</span>,
    },
    { id: 'p', header: 'Picked', cell: (w) => <Progress value={w.pickedUnits} total={w.units} /> },
    {
      id: 't',
      header: 'Open picks',
      align: 'right',
      cell: (w) => <span className="num">{w.openTasks}</span>,
    },
    {
      id: 's',
      header: 'Status',
      cell: (w) => (
        <Badge
          tone={
            w.status === 'complete'
              ? 'ok'
              : w.status === 'released'
                ? 'accent'
                : w.status === 'cancelled'
                  ? 'neutral'
                  : 'info'
          }
          dot
        >
          {w.status[0]!.toUpperCase() + w.status.slice(1)}
        </Badge>
      ),
    },
    ...(can('outbound:manage')
      ? [
          {
            id: 'a',
            header: '',
            align: 'right' as const,
            cell: (w: WaveDto) =>
              w.status === 'planned' ? (
                <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <Button size="sm" onClick={() => cancel.mutate(w.id)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    icon={Play}
                    onClick={() => release.mutate(w.id)}
                  >
                    Release
                  </Button>
                </div>
              ) : null,
          },
        ]
      : []),
  ];
  return (
    <div className="stack-lg">
      <PageHeader
        title="Waves"
        description="Group allocated orders by carrier, cut-off, ship date, retailer or client and release them together. Orders not in a wave can stream continuously from the Orders page."
        actions={
          <>
            {select}
            {can('outbound:manage') ? (
              <Button
                variant="primary"
                icon={Waves}
                disabled={!siteId}
                onClick={() => setPlanning(true)}
              >
                Plan a wave
              </Button>
            ) : null}
          </>
        }
      />
      <div>
        <Tabs
          value={status}
          onChange={setStatus}
          tabs={[
            { id: '', label: 'All' },
            { id: 'planned', label: 'Planned' },
            { id: 'released', label: 'Released' },
            { id: 'complete', label: 'Complete' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={q.data ?? []}
          rowKey={(w) => w.id}
          loading={q.isLoading}
          empty={
            <EmptyState icon={Waves} title="No waves">
              Plan a wave to pick several orders together.
            </EmptyState>
          }
        />
      </div>
      {planning ? <PlanWaveDialog siteId={siteId} onClose={() => setPlanning(false)} /> : null}
    </div>
  );
}

function PlanWaveDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const clients = useClients();
  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api<Page<PartnerDto>>('/trading-partners?limit=200'),
  });
  const [method, setMethod] = useState<PickMethod>('batch');
  const [clientId, setClientId] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [retailerId, setRetailerId] = useState('');
  const [type, setType] = useState('');
  const [shipBy, setShipBy] = useState(new Date().toISOString().slice(0, 10));
  const [maxOrders, setMaxOrders] = useState('100');
  const [cluster, setCluster] = useState('6');
  const [releaseNow, setReleaseNow] = useState(true);
  const preview = useQuery({
    queryKey: ['orders', 'wave-preview', siteId, clientId, shipBy, type],
    queryFn: () =>
      api<Page<OrderSummaryDto>>(
        `/orders?siteId=${siteId}&status=allocated&limit=200${clientId ? `&clientId=${clientId}` : ''}${type ? `&type=${type}` : ''}`,
      ),
  });
  const candidates = (preview.data?.items ?? []).filter(
    (o) => !o.waveNumber && o.shipByDate <= shipBy,
  );
  const plan = useAction(
    () =>
      api<WaveDto>('/waves', {
        method: 'POST',
        body: {
          siteId,
          pickMethod: method,
          clientId: clientId || undefined,
          carrierId: carrierId || undefined,
          retailerId: retailerId || undefined,
          type: type || undefined,
          shipBy: shipBy || undefined,
          maxOrders: Number(maxOrders),
          clusterSize: Number(cluster),
          release: releaseNow,
        },
      }),
    {
      invalidate: [['picking'], ['orders']],
      success: (w) =>
        `Wave ${w.number}: ${w.orders} orders${w.status === 'released' ? `, ${w.lists} pick list(s)` : ''}`,
      onSuccess: onClose,
    },
  );
  const parts = partners.data?.items ?? [];
  return (
    <Dialog
      wide
      title="Plan a wave"
      description="Allocated orders not yet in a wave, earliest cut-off first."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Waves}
            loading={plan.isPending}
            onClick={() => plan.mutate(undefined)}
          >
            {releaseNow ? 'Plan and release' : 'Plan wave'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="choice-list" role="radiogroup" aria-label="Pick method">
          {(Object.keys(METHOD) as PickMethod[]).map((m) => (
            <label key={m} className="check">
              <input
                type="radio"
                name="method"
                checked={method === m}
                onChange={() => setMethod(m)}
              />
              <span>
                <strong>{METHOD[m].label}</strong> <span className="muted">— {METHOD[m].help}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="form-grid-3">
          <Field label="Ship by (on or before)">
            <input
              className="input"
              type="date"
              value={shipBy}
              onChange={(e) => setShipBy(e.target.value)}
            />
          </Field>
          <Field label="Client">
            <select
              className="select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Any</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Order type">
            <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Any</option>
              <option value="parcel">Parcel</option>
              <option value="b2b">B2B</option>
            </select>
          </Field>
          <Field label="Carrier">
            <select
              className="select"
              value={carrierId}
              onChange={(e) => setCarrierId(e.target.value)}
            >
              <option value="">Any</option>
              {parts
                .filter((p) => p.type === 'carrier')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Retailer">
            <select
              className="select"
              value={retailerId}
              onChange={(e) => setRetailerId(e.target.value)}
            >
              <option value="">Any</option>
              {parts
                .filter((p) => p.type === 'retailer')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Max orders">
            <input
              className="input num"
              value={maxOrders}
              onChange={(e) => setMaxOrders(e.target.value)}
            />
          </Field>
          {method === 'cluster' ? (
            <Field label="Orders per cart">
              <input
                className="input num"
                value={cluster}
                onChange={(e) => setCluster(e.target.value)}
              />
            </Field>
          ) : null}
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={releaseNow}
            onChange={(e) => setReleaseNow(e.target.checked)}
          />{' '}
          Release to the floor immediately
        </label>
        <Callout tone={candidates.length ? 'info' : 'warn'}>
          {candidates.length
            ? `${candidates.length} allocated order(s) shipping by ${shipBy} are available${clientId || type ? ' for this filter' : ''}; carrier and retailer filters apply on planning.`
            : 'No allocated orders match yet.'}
        </Callout>
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------ picking

export function PickingPage() {
  const navigate = useNavigate();
  const { siteId, select } = useDefaultSite();
  const [state, setState] = useState<'active' | 'done'>('active');
  const q = useQuery({
    queryKey: ['picking', 'lists', siteId, state],
    queryFn: () => api<Page<PickListDto>>(`/pick-lists?siteId=${siteId}&state=${state}&limit=200`),
    enabled: Boolean(siteId),
    refetchInterval: 15_000,
  });
  const lists = q.data?.items ?? [];
  const openUnits = lists.reduce((a, l) => a + l.units - l.pickedUnits, 0);
  const columns: Column<PickListDto>[] = [
    {
      id: 'c',
      header: 'Pick list',
      cell: (l) => (
        <div>
          <div className="primary-cell mono">{l.code}</div>
          <div className="sub">{l.waveNumber ? `Wave ${l.waveNumber}` : 'Continuous'}</div>
        </div>
      ),
    },
    {
      id: 'm',
      header: 'Method',
      cell: (l) => (
        <div>
          {METHOD[l.method].label}
          {l.zone ? <div className="sub">Zone {l.zone}</div> : null}
        </div>
      ),
    },
    {
      id: 'o',
      header: 'Orders',
      align: 'right',
      cell: (l) => <span className="num">{l.orders}</span>,
    },
    {
      id: 'p',
      header: 'Progress',
      cell: (l) => <Progress value={l.pickedUnits} total={l.units} />,
    },
    {
      id: 't',
      header: 'Picks left',
      align: 'right',
      cell: (l) => (
        <span className="num">
          {l.openTasks} / {l.tasks}
        </span>
      ),
    },
    {
      id: 'a',
      header: 'Picker',
      cell: (l) => l.assignedTo ?? <span className="faint">Unassigned</span>,
    },
    {
      id: 's',
      header: 'Status',
      cell: (l) => (
        <Badge tone={l.state === 'done' ? 'ok' : l.state === 'in_progress' ? 'accent' : 'info'} dot>
          {l.state === 'in_progress' ? 'In progress' : l.state === 'done' ? 'Done' : 'Ready'}
        </Badge>
      ),
    },
  ];
  return (
    <div className="stack-lg">
      <PageHeader
        title="Picking"
        description="Pick lists released to the floor, sequenced by location pick sequence. Scanners use the same lists."
        actions={select}
      />
      <div className="grid-stats">
        <Stat
          label="Lists ready / in progress"
          icon={ClipboardList}
          value={state === 'active' ? formatNumber(lists.length) : '—'}
          foot={`${lists.filter((l) => l.state === 'in_progress').length} in progress`}
        />
        <Stat
          label="Units left to pick"
          icon={Boxes}
          tone={openUnits ? 'warn' : 'ok'}
          value={state === 'active' ? formatNumber(openUnits) : '—'}
          foot="Across active lists"
        />
      </div>
      <div>
        <Tabs
          value={state}
          onChange={setState}
          tabs={[
            { id: 'active', label: 'Active' },
            { id: 'done', label: 'Completed' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={lists}
          rowKey={(l) => l.id}
          loading={q.isLoading}
          onRowClick={(l) => void navigate(`/outbound/picking/${l.id}`)}
          empty={
            <EmptyState icon={ClipboardList} title="Nothing to pick">
              Release orders or a wave to create pick lists.
            </EmptyState>
          }
        />
      </div>
    </div>
  );
}

export function PickListPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['picking', 'list', id],
    queryFn: () => api<PickListDetailDto>(`/pick-lists/${id}`),
    refetchInterval: 15_000,
  });
  const [task, setTask] = useState<PickTaskDto | null>(null);
  const start = useAction(() => api(`/pick-lists/${id}/start`, { method: 'POST' }), {
    invalidate: [['picking']],
    success: 'Pick list assigned to you',
  });
  const l = q.data;
  if (q.isLoading) return <Skeleton height={300} />;
  if (!l) return <ErrorMessage error={q.error} />;
  const next = l.picks.find((p) => p.state === 'open');
  return (
    <div className="stack-lg">
      <div>
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowLeft}
          onClick={() => void navigate('/outbound/picking')}
        >
          Picking
        </Button>
      </div>
      <PageHeader
        title={`Pick list ${l.code}`}
        description={`${METHOD[l.method].label}${l.zone ? ` · zone ${l.zone}` : ''}${l.waveNumber ? ` · wave ${l.waveNumber}` : ''} · ${l.orders} order(s) · ${l.assignedTo ? `assigned to ${l.assignedTo}` : 'unassigned'}`}
        actions={
          can('outbound:pick') && l.state !== 'done' ? (
            <>
              {l.state === 'open' ? (
                <Button
                  icon={Play}
                  loading={start.isPending}
                  onClick={() => start.mutate(undefined)}
                >
                  Start
                </Button>
              ) : null}
              {next ? (
                <Button variant="primary" icon={ScanLine} onClick={() => setTask(next)}>
                  Next pick: {next.locationCode}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      <Card flush>
        <div className="card-body">
          <Progress value={l.pickedUnits} total={l.units} />
        </div>
      </Card>
      <Card title="Picks" sub="In walking order" flush>
        <PicksTable picks={l.picks} onConfirm={can('outbound:pick') ? setTask : undefined} />
      </Card>
      {task ? (
        <PickDialog
          task={task}
          onClose={() => setTask(null)}
          onNext={() => {
            const after = l.picks.find((p) => p.state === 'open' && p.id !== task.id);
            setTask(after ?? null);
          }}
        />
      ) : null}
    </div>
  );
}

function PickDialog({
  task,
  onClose,
  onNext,
}: {
  task: PickTaskDto;
  onClose: () => void;
  onNext: () => void;
}) {
  const [location, setLocation] = useState('');
  const [barcode, setBarcode] = useState('');
  const [qty, setQty] = useState(String(task.qty));
  const [tote, setTote] = useState('');
  const [reason, setReason] = useState('');
  const toast = useToast();
  const short = Number(qty) < task.qty;
  const confirm = useAction(
    () =>
      api<ConfirmPickResult>(`/pick-tasks/${task.id}/confirm`, {
        method: 'POST',
        body: {
          qty: Number(qty),
          locationCode: location || undefined,
          barcode: barcode || undefined,
          toteCode: tote || undefined,
          reasonCodeId: short ? reason : null,
        },
      }),
    {
      invalidate: [['picking'], ['orders'], ['inventory']],
      onSuccess: (r) => {
        if (r.shortQty)
          toast.error(
            `Short ${r.shortQty}: recount created${r.replenTaskId ? ', replenishment raised' : ''}${r.reassigned ? `, ${r.reassigned} re-allocated elsewhere` : ''}`,
          );
        else
          toast.ok(
            `Picked ${r.task.pickedQty} × ${task.sku}${r.task.toteCode ? ` into ${r.task.toteCode}` : ''}`,
          );
        onNext();
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!short || reason) confirm.mutate(undefined);
  };
  return (
    <Dialog
      title={`Pick ${task.sku}`}
      description={`Order ${task.orderNumber}${task.slot ? ` · tote slot ${task.slot}` : ''}`}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            type="submit"
            variant={short ? 'danger-solid' : 'primary'}
            loading={confirm.isPending}
            disabled={short && !reason}
          >
            {short ? `Confirm short (${task.qty - Number(qty)})` : 'Confirm pick'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="putaway-target">
          <div className="muted small">Go to</div>
          <div className="putaway-code mono">{task.locationCode}</div>
          <div className="small">
            Pick <strong>{formatNumber(task.qty)}</strong> × {task.description}
            {task.lot ? ` · lot ${task.lot}` : ''}
            {task.lpnCode ? ` · LPN ${task.lpnCode}` : ''}
          </div>
        </div>
        <div className="form-grid">
          <Field label="Scan location" hint="Optional check">
            <input
              className="input mono"
              autoFocus
              value={location}
              onChange={(e) => setLocation(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Scan item" hint="Optional check">
            <input
              className="input mono"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </Field>
          <Field label="Quantity picked">
            <input
              className="input num"
              inputMode="numeric"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </Field>
          <Field label="Into tote" hint="Blank = the order's tote">
            <input
              className="input mono"
              value={tote}
              onChange={(e) => setTote(e.target.value.toUpperCase())}
            />
          </Field>
        </div>
        {short ? (
          <>
            <Field label="Short reason">
              <ReasonSelect categories={['short']} value={reason} onChange={setReason} />
            </Field>
            <Callout tone="warn">
              The location gets a blind recount, its home location a replenishment, and the
              shortfall is re-allocated from other stock if possible.
            </Callout>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------ packing station

export function PackingPage() {
  const [params, setParams] = useSearchParams();
  const orderId = params.get('order') ?? '';
  const [scan, setScan] = useState('');
  const toast = useToast();
  const ready = useQuery({
    queryKey: ['orders', 'to-pack'],
    queryFn: () => api<Page<OrderSummaryDto>>('/orders?status=picked&limit=100'),
    refetchInterval: 20_000,
  });
  const find = async (e: FormEvent) => {
    e.preventDefault();
    const term = scan.trim();
    if (!term) return;
    const r = await api<Page<OrderSummaryDto>>(
      `/orders?status=active&q=${encodeURIComponent(term)}&limit=5`,
    );
    const hit = r.items.find((o) => o.number.toUpperCase() === term.toUpperCase()) ?? r.items[0];
    if (hit) {
      setParams({ order: hit.id });
      setScan('');
    } else toast.error(`No active order matches ${term}`);
  };
  return (
    <div className="stack-lg">
      <PageHeader
        title="Packing station"
        description="Scan an order, open a carton, scan every item into it, weigh and close. Each carton gets an SSCC label; build pallets for B2B."
      />
      <Card>
        <form
          onSubmit={(e) => void find(e)}
          className="row"
          style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
        >
          <Field label="Scan order or tote">
            <input
              className="input mono"
              style={{ minWidth: 280 }}
              autoFocus
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              placeholder="Order number"
            />
          </Field>
          <Button type="submit" icon={ScanLine} disabled={!scan.trim()}>
            Open order
          </Button>
        </form>
      </Card>
      {orderId ? (
        <PackOrder id={orderId} onDone={() => setParams({})} />
      ) : (
        <Card title="Picked, waiting for packing" flush>
          <DataTable
            columns={[
              {
                id: 'n',
                header: 'Order',
                cell: (o: OrderSummaryDto) => <span className="mono">{o.number}</span>,
              },
              { id: 'c', header: 'Client', cell: (o: OrderSummaryDto) => o.clientName },
              {
                id: 'u',
                header: 'Units',
                align: 'right',
                cell: (o: OrderSummaryDto) => (
                  <span className="num">{formatNumber(o.pickedUnits)}</span>
                ),
              },
              {
                id: 'k',
                header: 'Cut-off',
                cell: (o: OrderSummaryDto) => (
                  <span className={o.atRisk ? 'warn-text' : ''}>{cutoffLabel(o.cutoffAt)}</span>
                ),
              },
            ]}
            rows={ready.data?.items ?? []}
            rowKey={(o) => o.id}
            onRowClick={(o) => setParams({ order: o.id })}
            empty={
              <EmptyState icon={PackageOpen} title="Nothing waiting">
                Picked orders appear here.
              </EmptyState>
            }
          />
        </Card>
      )}
    </div>
  );
}

function PackOrder({ id, onDone }: { id: string; onDone: () => void }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['orders', 'detail', id],
    queryFn: () => api<OrderDetailDto>(`/orders/${id}`),
  });
  const [cartonType, setCartonType] = useState('M');
  const [scan, setScan] = useState('');
  const [qty, setQty] = useState('1');
  const [weight, setWeight] = useState('');
  const [label, setLabel] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const ref = useRef<HTMLInputElement>(null);
  const o = q.data;
  const open = o?.cartons.find((c) => c.state === 'open');
  const openCarton = useAction(
    () => api<CartonDto>(`/orders/${id}/cartons`, { method: 'POST', body: { cartonType } }),
    {
      invalidate: [['orders']],
      success: (c) => `Carton ${c.sscc} opened`,
      onSuccess: () => setTimeout(() => ref.current?.focus(), 50),
    },
  );
  const pack = useAction(
    () =>
      api<CartonDto>(`/cartons/${open!.id}/items`, {
        method: 'POST',
        body: { barcode: scan.trim(), qty: Number(qty) || 1 },
      }),
    {
      invalidate: [['orders']],
      success: 'Packed',
      onSuccess: () => {
        setScan('');
        setQty('1');
        ref.current?.focus();
      },
    },
  );
  const close = useAction(
    () =>
      api<CartonDto>(`/cartons/${open!.id}/close`, {
        method: 'POST',
        body: { weightG: weight ? Math.round(Number(weight) * 1000) : null },
      }),
    {
      invalidate: [['orders']],
      success: (c) => `Carton ${c.sscc} closed`,
      onSuccess: (c) => {
        setWeight('');
        setLabel(c.lpnId);
      },
    },
  );
  const packAll = useAction(
    () =>
      api<OrderDetailDto>(`/orders/${id}/pack-remaining`, {
        method: 'POST',
        body: {
          cartonType: o?.type === 'b2b' ? 'pallet' : 'M',
          weightG: weight ? Math.round(Number(weight) * 1000) : null,
        },
      }),
    {
      invalidate: [['orders']],
      success: 'Everything packed',
    },
  );
  const pallet = useAction(
    () =>
      api<OrderDetailDto>(`/orders/${id}/pallets`, {
        method: 'POST',
        body: { cartonIds: selected },
      }),
    {
      invalidate: [['orders']],
      success: 'Pallet built with its own SSCC',
      onSuccess: () => setSelected([]),
    },
  );
  if (q.isLoading) return <Skeleton height={300} />;
  if (!o) return <ErrorMessage error={q.error} />;
  const remaining = o.lineItems
    .map((l) => ({ ...l, left: l.pickedQty - l.packedQty }))
    .filter((l) => l.left > 0);
  const closedLoose = o.cartons.filter(
    (c) => c.state === 'closed' && !c.palletSscc && c.cartonType !== 'pallet',
  );
  return (
    <div className="stack-lg">
      <Card
        title={
          <span className="row" style={{ gap: 8 }}>
            Order <span className="mono">{o.number}</span> <OrderStatusBadge status={o.status} />
          </span>
        }
        sub={`${o.clientName} · ${o.shipTo?.name ?? o.shipToName ?? ''} · cut-off ${cutoffLabel(o.cutoffAt)}`}
        actions={
          <div className="row" style={{ gap: 6 }}>
            <Button onClick={onDone}>Done</Button>
            {o.status === 'packed' && can('outbound:ship') ? (
              <Button
                variant="primary"
                icon={Send}
                onClick={() => void navigate(`/outbound/orders/${o.id}`)}
              >
                Ship confirm
              </Button>
            ) : null}
          </div>
        }
      >
        {remaining.length ? (
          <div className="stack-sm">
            <div className="section-title">Still to pack</div>
            {remaining.map((l) => (
              <div key={l.id} className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  <span className="mono">{l.sku}</span> — {l.description}
                </span>
                <Badge tone="warn">{l.left} left</Badge>
              </div>
            ))}
          </div>
        ) : (
          <Callout tone="ok" title="All picked units are packed">
            {o.status === 'packed' ? 'Ready to ship.' : 'Close any open carton to finish.'}
          </Callout>
        )}
      </Card>
      {o.status === 'picking' || o.status === 'picked' ? (
        <Card title={open ? `Packing into ${open.sscc}` : 'Open a carton'}>
          {open ? (
            <div className="stack">
              <form
                className="row"
                style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (scan.trim()) pack.mutate(undefined);
                }}
              >
                <Field label="Scan item">
                  <input
                    ref={ref}
                    className="input mono"
                    style={{ minWidth: 260 }}
                    autoFocus
                    value={scan}
                    onChange={(e) => setScan(e.target.value)}
                  />
                </Field>
                <Field label="Qty">
                  <input
                    className="input num"
                    style={{ width: 80 }}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </Field>
                <Button type="submit" icon={ScanLine} loading={pack.isPending}>
                  Pack
                </Button>
              </form>
              <div className="small muted">
                In this carton:{' '}
                {open.contents.length
                  ? open.contents.map((c) => `${c.sku} × ${c.qty}`).join(', ')
                  : 'nothing yet'}
              </div>
              <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
                <Field
                  label="Weight (kg)"
                  hint={o.type === 'parcel' ? 'Required for parcel' : 'Optional'}
                >
                  <input
                    className="input num"
                    style={{ width: 120 }}
                    inputMode="decimal"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                  />
                </Field>
                <Button
                  variant="primary"
                  icon={CheckCircle2}
                  loading={close.isPending}
                  disabled={!open.contents.length}
                  onClick={() => close.mutate(undefined)}
                >
                  Close carton
                </Button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="Carton">
                <select
                  className="select"
                  value={cartonType}
                  onChange={(e) => setCartonType(e.target.value)}
                >
                  {['S', 'M', 'L', 'XL', 'pallet'].map((c) => (
                    <option key={c} value={c}>
                      {c === 'pallet' ? 'Pallet' : `Carton ${c}`}
                    </option>
                  ))}
                </select>
              </Field>
              <Button
                variant="primary"
                icon={PackageOpen}
                loading={openCarton.isPending}
                disabled={!remaining.length}
                onClick={() => openCarton.mutate(undefined)}
              >
                Open carton
              </Button>
              {o.status === 'picked' && remaining.length ? (
                <Button
                  icon={PackageCheck}
                  loading={packAll.isPending}
                  onClick={() => packAll.mutate(undefined)}
                >
                  Pack everything {o.type === 'b2b' ? '(pallets as picked)' : 'in one carton'}
                </Button>
              ) : null}
            </div>
          )}
        </Card>
      ) : null}
      {o.cartons.length ? (
        <Card
          title="Cartons"
          flush
          actions={
            o.type === 'b2b' && closedLoose.length ? (
              <Button
                size="sm"
                icon={Layers}
                disabled={!selected.length}
                loading={pallet.isPending}
                onClick={() => pallet.mutate(undefined)}
              >
                Build pallet from {selected.length || 'selected'}
              </Button>
            ) : null
          }
        >
          {o.type === 'b2b' && closedLoose.length ? (
            <div className="card-body row" style={{ gap: 12, flexWrap: 'wrap' }}>
              {closedLoose.map((c) => (
                <label key={c.id} className="check">
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={(e) =>
                      setSelected((s) =>
                        e.target.checked ? [...s, c.id] : s.filter((x) => x !== c.id),
                      )
                    }
                  />
                  <span className="mono">{c.sscc}</span>
                </label>
              ))}
            </div>
          ) : null}
          <CartonsTable cartons={o.cartons} />
          <div className="card-body row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {o.cartons.map((c) => (
              <Button key={c.id} size="sm" icon={Printer} onClick={() => setLabel(c.lpnId)}>
                Label {c.sscc.slice(-6)}
              </Button>
            ))}
          </div>
        </Card>
      ) : null}
      {label ? <LpnLabelDialog lpnId={label} onClose={() => setLabel(null)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ replenishment

export function ReplenishmentPage() {
  const { can } = useAuth();
  const { siteId, select } = useDefaultSite();
  const [state, setState] = useState<'open' | 'done'>('open');
  const q = useQuery({
    queryKey: ['picking', 'replen', siteId, state],
    queryFn: () =>
      api<Page<ReplenTaskDto>>(`/replenishments?siteId=${siteId}&state=${state}&limit=200`),
    enabled: Boolean(siteId),
    refetchInterval: 20_000,
  });
  const generate = useAction(
    () =>
      api<{ created: number }>('/replenishments/generate', { method: 'POST', body: { siteId } }),
    {
      invalidate: [['picking']],
      success: (r) =>
        r.created
          ? `${r.created} replenishment task(s) created`
          : 'Every home location is above its minimum',
    },
  );
  const confirm = useAction(
    (id: string) =>
      api<{ moved: number }>(`/replenishments/${id}/confirm`, { method: 'POST', body: {} }),
    {
      invalidate: [['picking'], ['inventory'], ['home-locations']],
      success: (r) => `Moved ${r.moved} units into the pick face`,
    },
  );
  const columns: Column<ReplenTaskDto>[] = [
    {
      id: 'i',
      header: 'Item',
      cell: (r) => (
        <div>
          <div className="primary-cell mono">{r.sku}</div>
          <div className="sub">{r.description}</div>
        </div>
      ),
    },
    {
      id: 'f',
      header: 'From',
      cell: (r) => <span className="mono">{r.fromLocationCode ?? '—'}</span>,
    },
    { id: 't', header: 'To (home)', cell: (r) => <span className="mono">{r.toLocationCode}</span> },
    {
      id: 'q',
      header: 'Qty',
      align: 'right',
      cell: (r) => <span className="num">{formatNumber(r.qty)}</span>,
    },
    {
      id: 'r',
      header: 'Why',
      cell: (r) => (
        <Badge tone={r.reason === 'short' ? 'warn' : 'info'}>
          {r.reason === 'short' ? 'Short pick' : 'Below min'}
        </Badge>
      ),
    },
    {
      id: 'w',
      header: 'Raised',
      cell: (r) => (
        <span className="small">
          {timeAgo(r.createdAt)}
          {r.completedBy ? ` · done by ${r.completedBy}` : ''}
        </span>
      ),
    },
    ...(state === 'open' && can('outbound:pick')
      ? [
          {
            id: 'a',
            header: '',
            align: 'right' as const,
            cell: (r: ReplenTaskDto) => (
              <Button size="sm" variant="primary" icon={Truck} onClick={() => confirm.mutate(r.id)}>
                Done
              </Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <div className="stack-lg">
      <PageHeader
        title="Replenishment"
        description="Moves reserve stock into home pick faces: raised by short picks and by min/max."
        actions={
          <>
            {select}
            {can('outbound:manage') ? (
              <Button
                icon={RefreshCcw}
                loading={generate.isPending}
                disabled={!siteId}
                onClick={() => generate.mutate(undefined)}
              >
                Run min/max
              </Button>
            ) : null}
          </>
        }
      />
      <div>
        <Tabs
          value={state}
          onChange={setState}
          tabs={[
            { id: 'open', label: 'Open' },
            { id: 'done', label: 'Completed' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={q.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          empty={
            <EmptyState icon={XCircle} title="No replenishments">
              Pick faces are full.
            </EmptyState>
          }
        />
      </div>
    </div>
  );
}
